import * as React from "react";
import { CloudUpload, Loader2 } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { ACCEPTED_TYPES } from "./studentImportShared";

/**
 * Phase H6d — drag-and-drop CSV dropzone, extracted from the wizard's
 * `<UploadState>` so the standing importer reuses the exact same
 * affordance + copy. Keep the visual treatment in lockstep across the
 * two orchestrators — admins shouldn't perceive the standing surface
 * as "lesser" than the wizard.
 *
 * The parent owns the file-input ref + change handlers so it can
 * reset the input via the ref after a successful commit (resetting
 * `value=""` is how the browser allows re-uploading the same file).
 */
export interface UploadDropzoneProps {
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  isPreviewPending: boolean;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: React.DragEvent<HTMLLabelElement>) => void;
  /** Stable id passed to the underlying <input>; lets multiple
   *  dropzones coexist on a page if needed and avoids id collisions
   *  with the wizard's instance when both pages exist in the bundle. */
  inputId?: string;
}

export function UploadDropzone({
  fileInputRef,
  isPreviewPending,
  onFileInputChange,
  onDrop,
  inputId = "student-import-file",
}: UploadDropzoneProps) {
  return (
    <label
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDrop={onDrop}
      className={cn(
        "border-border bg-card hover:border-primary/50 hover:bg-muted/40 group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors duration-200",
        isPreviewPending && "pointer-events-none opacity-70",
      )}
      data-testid="student-import-dropzone"
    >
      <span
        className="bg-primary/10 text-primary inline-flex size-10 items-center justify-center rounded-full"
        aria-hidden="true"
      >
        {isPreviewPending ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <CloudUpload className="size-5" />
        )}
      </span>
      <div className="grid gap-1">
        <p className="text-foreground text-sm font-medium">
          {isPreviewPending ? "Validating your file…" : "Drop a CSV here, or click to browse"}
        </p>
        <p className="text-muted-foreground text-xs">
          CSV only · 1 MB max · {ACCEPTED_TYPES.split(",").length} formats accepted
        </p>
      </div>
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={ACCEPTED_TYPES}
        className="sr-only"
        onChange={onFileInputChange}
        disabled={isPreviewPending}
      />
    </label>
  );
}
